import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CardBody } from 'reactstrap';
import { translate } from "react-translate";
import { Line } from 'react-chartjs-2';
import { actionGetKafkaList } from 'store/actions/kafkaActions';
import TableKafka from './TableKafka';
import Paginator from '../Common/Paginator';
import Switch from '../Common/Switch';
import LoadingGlobal from "plugins/TS-lib-utils-public/components/Loading/LoadingGlobal";
import Select from 'react-select';

// Initialize with empty data points for smoother transitions
const INITIAL_DATA_POINTS = 15;
const initialLabels = Array(INITIAL_DATA_POINTS).fill('');
const initialData = Array(INITIAL_DATA_POINTS).fill(0);

// Status options for the select dropdown
const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'processed', label: 'Processed' },
    { value: 'failed', label: 'Failed' },
    { value: 'ignored', label: 'Ignored' },
    { value: 'retrying', label: 'Retrying' },
    { value: 'skipped', label: 'Skipped' },
    { value: 'no_changes', label: 'No Changes' }
];

// Memoize table component to prevent unnecessary re-renders
const MemoizedTableKafka = React.memo(TableKafka);

const ListKafka = (props) => {
    const dispatch = useDispatch();
    const { t } = props;

    const { messages } = useSelector(state => state.kafkaReducer?.kafka ?? {});
    const { pagination } = useSelector(state => state.kafkaReducer?.kafka ?? {});

    const [loading, setLoading] = useState(false);
    const [loadingLocal, setLoadingLocal] = useState(false);
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(20);
    const [totalPage, setTotalPage] = useState(0);
    const [filter, setFilter] = useState(null);
    const [realtimeActive, setRealtimeActive] = useState(false);
    const [searchParams, setSearchParams] = useState({
        event: '',
        sender: '',
        topic: '',
        consumer: '',
        status: null,
        dateFrom: null,
        dateTo: null
    });
    
    // Use refs for chart data to prevent unnecessary re-renders
    const chartDataRef = useRef({
        labels: [...initialLabels],
        datasets: [
            {
                data: [...initialData],
                borderColor: 'rgb(75, 192, 192)',
                backgroundColor: 'rgba(75, 192, 192, 0.5)',
                tension: 0.1,
                fill: false,
                pointRadius: 4,
                pointHoverRadius: 6
            }
        ]
    });
    
    const [chartData, setChartData] = useState(chartDataRef.current);
    const previousMessages = useRef([]); // Track previous messages to compare with new ones
    const refreshCount = useRef(0);
    const intervalRef = useRef(null);
    const activeFilterRef = useRef(null);
    const chartRef = useRef(null);
    const messagesRef = useRef(messages); // Track messages with ref

    // Update messages ref when messages change
    useEffect(() => {
        messagesRef.current = messages;
    }, [messages]);

    useEffect(() => {
        const currentPage = page !== null && page !== undefined ? page : 1;
        const currentLimit = limit !== null && limit !== undefined ? limit : 20;

        dispatch(actionGetKafkaList(currentPage, currentLimit));
        
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [page, limit, dispatch]);

    useEffect(() => {
        if (messages && pagination) {
            if (realtimeActive) {
                // Find new messages by comparing with previous state
                const newMessages = findNewMessages(previousMessages.current, messages);
                
                // Always add a data point, even if it's 0
                updateChartData(newMessages.length);
                
                // Update previous messages for next comparison
                previousMessages.current = [...messages];
                refreshCount.current += 1;
            }
            
            setLoading(false);
            setLoadingLocal(false);
            setPage(pagination._page);
            setLimit(pagination._limit);
            setTotalPage(pagination._total_pages);
        }
    }, [messages, pagination]);

    // Function to find new messages by comparing with previous state
    const findNewMessages = (oldMessages, newMessages) => {
        if (!oldMessages || oldMessages.length === 0) return newMessages;
        
        // Get the latest timestamp from previous messages
        const latestTimestamp = Math.max(...oldMessages.map(msg => new Date(msg.createdAt).getTime()));
        
        // Filter messages that are newer than the latest timestamp we had
        return newMessages.filter(msg => {
            const msgTimestamp = new Date(msg.createdAt).getTime();
            return msgTimestamp > latestTimestamp;
        });
    };

    const updateChartData = (newMessagesCount) => {
        const now = new Date();
        const timeLabel = `${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
        
        // Update the ref first
        chartDataRef.current = {
            labels: [...chartDataRef.current.labels.slice(1), timeLabel],
            datasets: [
                {
                    ...chartDataRef.current.datasets[0],
                    data: [...chartDataRef.current.datasets[0].data.slice(1), newMessagesCount]
                }
            ]
        };
        
        // Then update state - this will be batched by React
        setChartData(chartDataRef.current);
        
        // If we have a chart reference, update it directly for smoother transitions
        if (chartRef.current) {
            const chart = chartRef.current.chartInstance;
            if (chart) {
                chart.data = chartDataRef.current;
                chart.update('quiet'); // 'quiet' prevents animation which reduces flicker
            }
        }
    };

    const intervalOnOff = (isChecked, filter) => {
        if (intervalRef.current) {
            stopAutoRefresh();
        }

        if (isChecked === true) {
            setRealtimeActive(true);
            // Initialize with current messages
            previousMessages.current = [...(messagesRef.current || [])];
            refreshCount.current = 0;
            resetChartData();
            activeFilterRef.current = filter;
            startAutoRefresh(filter);
        } else {
            setRealtimeActive(false);
            stopAutoRefresh();
        }
    };

    const resetChartData = () => {
        chartDataRef.current = {
            labels: [...initialLabels],
            datasets: [
                {
                    label: t('chart.new-messages-label'),
                    data: [...initialData],
                    borderColor: 'rgb(75, 192, 192)',
                    backgroundColor: 'rgba(75, 192, 192, 0.5)',
                    tension: 0.1,
                    fill: false,
                    pointRadius: 4,
                    pointHoverRadius: 6
                }
            ]
        };
        setChartData(chartDataRef.current);
    };

    const startAutoRefresh = () => {
        intervalRef.current = setInterval(() => {
            changeDataSearch(0, activeFilterRef.current);
        }, 2000);
    };

    const stopAutoRefresh = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    };

    const handleSearchParamChange = (name, value) => {
        // Manejo especial para fechas
        if (name === 'dateFrom' || name === 'dateTo') {
            setSearchParams(prev => ({
            ...prev,
            [name]: value // Guarda el string directamente en lugar de Date object
            }));
        } else {
            setSearchParams(prev => ({
            ...prev,
            [name]: value
            }));
        }
    };

    const buildSearchQuery = () => {
        const queryParts = [];
        
        if (searchParams.event) queryParts.push(`event=${encodeURIComponent(searchParams.event)}`);
        if (searchParams.sender) queryParts.push(`sender=${encodeURIComponent(searchParams.sender)}`);
        if (searchParams.topic) queryParts.push(`topic=${encodeURIComponent(searchParams.topic)}`);
        if (searchParams.consumer) queryParts.push(`consumer=${encodeURIComponent(searchParams.consumer)}`);
        if (searchParams.status) queryParts.push(`status=${encodeURIComponent(searchParams.status.value)}`);
        if (searchParams.dateFrom) queryParts.push(`dateFrom=${searchParams.dateFrom}`);
        if (searchParams.dateTo) queryParts.push(`dateTo=${searchParams.dateTo}`);
        
        return queryParts.join('&');
    };

    const handleSearch = () => {
        const query = buildSearchQuery();
        changeDataSearch(0, query);
    };

    const handleReset = () => {
        setSearchParams({
            event: '',
            sender: '',
            topic: '',
            consumer: '',
            status: null,
            dateFrom: null,
            dateTo: null
        });
        changeDataSearch(0, null);
    };

    const changeDataSearch = (page = 1, filter) => {
        if (page !== null && page !== undefined) {
            page = 1;
        }

        if(limit !== null && limit !== undefined) {
            limit = 20;
        }

        if (filter == null) {
            setFilter(null);
            dispatch(actionGetKafkaList(page, limit));
                
        } else {
            setFilter(filter);
            dispatch(actionGetKafkaList(page, limit, filter));
        }
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        title: {
            display: false,
            text: t("chart.realtime-activity-title"),
            fontSize: 16
        },
        legend: {
            display: false,
            position: 'top'
        },
        tooltips: {
            callbacks: {
                label: function(tooltipItem) {
                    return t('chart.new-messages-tooltip', { count: tooltipItem.yLabel });
                }
            }
        },
        scales: {
            yAxes: [{
                ticks: {
                    beginAtZero: true,
                    stepSize: 1,
                    suggestedMax: 5
                },
                scaleLabel: {
                    display: true,
                    labelString: t("chart.new-messages-label")
                }
            }],
            xAxes: [{
                ticks: {
                    autoSkip: true,
                    maxTicksLimit: 10
                },
                scaleLabel: {
                    display: true,
                    labelString: t("chart.time-label")
                }
            }]
        },
        animation: {
            duration: 1000,
            easing: 'easeOutQuart'
        },
        elements: {
            line: {
                tension: 0.4
            }
        }
    };

    // Memoize table data to prevent unnecessary re-renders
    const tableData = useMemo(() => messages, [messages]);

    return (
        <div className='content user-manual'>           
            <CardBody>
                <div>
                    <LoadingGlobal loading={loading} />
                    <Switch 
                        label={t("switch.realtime-label")} 
                        callback={intervalOnOff} 
                        filter={filter}
                    />

                    {realtimeActive && (
                        <div className="realtime-chart mb-4" style={{ height: '250px' }}>
                            <Line 
                                ref={chartRef}
                                data={chartData} 
                                options={chartOptions}
                                redraw={false}
                            />
                        </div>
                    )}

                    <div className="search-filters m-0 pd0">
                        <div className="d-flex flex-wrap align-items-end" style={{ gap: '8px' }}>
                            <div className="pr-2 pl-0 pt-2 pb-2" style={{ flex: '1 1 150px', minWidth: '150px' }}>
                            <input
                                type="text"
                                className="form-control"
                                placeholder={t("search.event-placeholder")}
                                value={searchParams.event ?? ''}
                                onChange={(e) => handleSearchParamChange('event', e.target.value)}
                            />
                            </div>
                            <div className="pr-2 pl-0 pt-2 pb-2"  style={{ flex: '1 1 150px', minWidth: '150px' }}>
                            <input
                                type="text"
                                className="form-control"
                                placeholder={t("search.sender-placeholder")}
                                value={searchParams.sender ?? ''}
                                onChange={(e) => handleSearchParamChange('sender', e.target.value)}
                            />
                            </div>
                            <div className="pr-2 pl-0 pt-2 pb-2"  style={{ flex: '1 1 150px', minWidth: '150px' }}>
                            <input
                                type="text"
                                className="form-control"
                                placeholder={t("search.topic-placeholder")}
                                value={searchParams.topic ?? ''}
                                onChange={(e) => handleSearchParamChange('topic', e.target.value)}
                            />
                            </div>
                            <div  className="pr-2 pl-0 pt-2 pb-2" style={{ flex: '1 1 150px', minWidth: '150px' }}>
                            <input
                                type="text"
                                className="form-control"
                                placeholder={t("search.consumer-placeholder")}
                                value={searchParams.consumer ?? ''}
                                onChange={(e) => handleSearchParamChange('consumer', e.target.value)}
                            />
                            </div>
                            <div  className="pr-2 pl-0 pt-2 pb-2" style={{ flex: '1 1 150px', minWidth: '150px' }}>
                            <Select
                                options={statusOptions.map(option => ({
                                    ...option,
                                    label: t(`status.${option.value}`)
                                }))}
                                placeholder={t("search.status-placeholder")}
                                value={searchParams.status ?? ''}
                                onChange={(selected) => handleSearchParamChange('status', selected)}
                                isClearable
                            />
                            </div>
                            <div  className="pr-2 pl-0 pt-2 pb-2" style={{ flex: '1 1 180px', minWidth: '180px' }}>
                            <input
                                type="datetime-local"
                                className="form-control"
                                value={searchParams.dateFrom ?? ''}
                                onChange={(e) => handleSearchParamChange('dateFrom', e.target.value)}
                            />
                            </div>
                            <div  className="pr-2 pl-0 pt-2 pb-2" style={{ flex: '1 1 180px', minWidth: '180px' }}>
                            <input
                                type="datetime-local"
                                className="form-control"
                                value={searchParams.dateTo ?? ''}
                                onChange={(e) => handleSearchParamChange('dateTo', e.target.value)}
                            />
                            </div>
                            <div className="d-flex" style={{ gap: '8px', flex: '0 0 auto' }}>
                            <button className="btn btn-primary" onClick={handleSearch}>
                                {t("search.search-button")}
                            </button>
                            <button className="btn btn-secondary" onClick={handleReset}>
                                {t("search.reset-button")}
                            </button>
                            </div>
                        </div>
                    </div>
                    <MemoizedTableKafka data={tableData} loading={loadingLocal} />
                    <Paginator 
                        totalPages={totalPage} 
                        currentPage={page} 
                        pageSize={limit} 
                        filter={filter} 
                        callback={changeDataSearch} 
                    />
                </div>
            </CardBody>
        </div>
    );
};

export default translate("kafka")(ListKafka);